import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { Logo } from "../components/Navbar";

export default function Register() {
  const { register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    country: "",
    company: "",
    role: params.get("role") === "seller" ? "seller" : "buyer",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register(form);
      toast.success(t("create_acc"));
      navigate(user.role === "seller" ? "/seller" : "/");
    } catch (err) {
      toast.error("Could not create account", { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20";

  return (
    <div data-testid="register-page" className="min-h-[70vh] flex items-center justify-center bg-[#F8FAF7] px-4 py-16">
      <div className="w-full max-w-md rounded-3xl border border-teal-900/10 bg-white p-8 shadow-xl shadow-teal-900/5">
        <div className="flex justify-center"><Logo /></div>
        <h1 className="font-heading mt-6 text-center text-2xl font-bold text-slate-900">{t("reg_t")}</h1>
        <p className="mt-1 text-center text-sm text-slate-500">{t("reg_sub")}</p>

        <div className="mt-6 grid grid-cols-2 gap-2 rounded-xl bg-slate-100 p-1">
          {[
            { v: "buyer", l: t("i_buy") },
            { v: "seller", l: t("i_sell") },
          ].map((r) => (
            <button
              key={r.v}
              type="button"
              data-testid={`register-role-${r.v}`}
              onClick={() => setForm({ ...form, role: r.v })}
              className={`rounded-lg py-2 text-sm font-semibold transition ${
                form.role === r.v ? "bg-teal-700 text-white shadow" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {r.l}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label htmlFor="reg-name" className="text-sm font-medium text-slate-700">{t("full_name")}</label>
            <input id="reg-name" data-testid="register-name-input" required value={form.name} onChange={set("name")} className={inputCls} />
          </div>
          <div>
            <label htmlFor="reg-email" className="text-sm font-medium text-slate-700">{t("email")}</label>
            <input id="reg-email" data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
          </div>
          <div>
            <label htmlFor="reg-password" className="text-sm font-medium text-slate-700">{t("password")}</label>
            <input id="reg-password" data-testid="register-password-input" type="password" required minLength={8} value={form.password} onChange={set("password")} className={inputCls} />
          </div>
          {form.role === "seller" && (
            <div>
              <label htmlFor="reg-company" className="text-sm font-medium text-slate-700">{t("company")}</label>
              <input id="reg-company" data-testid="register-company-input" value={form.company} onChange={set("company")} className={inputCls} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="reg-phone" className="text-sm font-medium text-slate-700">{t("phone")}</label>
              <input id="reg-phone" data-testid="register-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} />
            </div>
            <div>
              <label htmlFor="reg-country" className="text-sm font-medium text-slate-700">{t("country")}</label>
              <input id="reg-country" data-testid="register-country-input" value={form.country} onChange={set("country")} className={inputCls} />
            </div>
          </div>
          <button
            data-testid="register-submit-button"
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-teal-700 py-3 text-sm font-bold text-white shadow-md shadow-teal-700/25 hover:bg-teal-800 transition active:scale-95 disabled:opacity-60"
          >
            {loading ? t("creating") : form.role === "seller" ? t("create_seller") : t("create_btn")}
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-slate-600">
          {t("have_acc")}{" "}
          <Link data-testid="register-to-login-link" to="/login" className="font-semibold text-teal-700 hover:underline">
            {t("login_btn")}
          </Link>
        </p>
      </div>
    </div>
  );
}
